/**
 * GeoLinkRulesPanel.tsx
 * Displays Pending Rules, Completed Rules, or Rejected Rules from GeoLink.
 * Matches GeoLink's wallet provider dashboard tabs (sections 3, 5, 6 of the API docs).
 *
 * mode="pending"   → GET /api/contracts/rules/pending       (non-deposit, awaiting confirmation)
 * mode="completed" → GET /api/contracts/rules/completed     (successfully executed)
 * mode="rejected"  → GET /api/contracts/rules/rejected      (cancelled / rejected)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  listPendingRules,
  listCompletedRules,
  listRejectedRules,
  rejectPendingRule,
  type GeoLinkPendingRule,
  type GeoLinkCompletedRule,
  type GeoLinkRejectedRule,
} from '../v2/services/geolinkRulesService';
import { RuleMatchDetailOverlay, type RuleMatchInfo } from './RuleMatchDetailOverlay';

// ── Color tokens (same palette as GeoLinkPendingDepositsPanel) ────────────────
const C = {
  bg:          '#fff',
  bgCard:      '#f8f9fa',
  bgCardHover: '#f0f0f0',
  border:      '#ddd',
  borderFaint: '#eee',
  text:        '#333',
  textMuted:   '#555',
  textFaint:   '#888',
  gold:        '#FFD700',
  goldDark:    '#996600',
  goldDim:     'rgba(255,215,0,0.15)',
  green:       '#28a745',
  greenDim:    'rgba(40,167,69,0.1)',
  amber:       '#fd7e14',
  amberDim:    'rgba(253,126,20,0.1)',
  blue:        '#007bff',
  blueDim:     'rgba(0,123,255,0.1)',
  red:         '#dc3545',
  redDim:      'rgba(220,53,69,0.1)',
};

type Mode = 'pending' | 'completed' | 'rejected';

const MODE_CONFIG = {
  pending:   { label: 'Pending', color: C.amber, dim: C.amberDim, border: '#fd7e1444', icon: '⏳', emptyMsg: 'No pending rules. Rules requiring confirmation will appear here when they fire.' },
  completed: { label: 'Completed', color: C.green, dim: C.greenDim, border: '#28a74544', icon: '✓', emptyMsg: 'No completed rules yet. Successfully executed rules will appear here.' },
  rejected:  { label: 'Rejected', color: C.red, dim: C.redDim, border: '#dc354544', icon: '✕', emptyMsg: 'No rejected rules.' },
};

interface Props {
  mode: Mode;
  walletPublicKey?: string | null;
  network?: 'testnet' | 'mainnet';
  pollIntervalMs?: number;
  allowedCountries?: Set<number>;
}

type AnyRule = GeoLinkPendingRule | GeoLinkCompletedRule | GeoLinkRejectedRule;

export const GeoLinkRulesPanel: React.FC<Props> = ({
  mode,
  walletPublicKey,
  network = 'testnet',
  pollIntervalMs = 15_000,
  allowedCountries,
}) => {
  const [rules, setRules]       = useState<AnyRule[]>([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [expandedId, setExpandedId]     = useState<string | null>(null);
  const [actioning, setActioning]       = useState<string | null>(null);
  const [ruleDetailInfo, setRuleDetailInfo] = useState<RuleMatchInfo | null>(null);

  const cfg = MODE_CONFIG[mode];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (mode === 'pending') {
        const data = await listPendingRules(walletPublicKey);
        setRules(data.pending_rules || []);
      } else if (mode === 'completed') {
        // Scope to the connected wallet on the server per the multi-tenant
        // scoping integration guide §3.1. The legacy unscoped call leaked
        // every tenant's history into Wallet B's UI, which is the bug we're
        // closing here. Passing `null` (no wallet) preserves the old
        // unscoped behaviour for admin contexts.
        const data = await listCompletedRules(200, walletPublicKey);
        const raw = (data.completed_rules || []) as GeoLinkCompletedRule[];
        // Client-side dedup: collapse records with the same tx hash (safety net)
        const seen = new Set<string>();
        const deduped = raw.filter(r => {
          const key = r.transaction_hash ||
            `${r.rule_id}::${r.matched_public_key}::${r.completed_at?.slice(0, 10) ?? ''}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setRules(deduped);
      } else {
        // Same scoping rationale as completed above.
        const data = await listRejectedRules(200, walletPublicKey);
        const raw = (data.rejected_rules || []) as GeoLinkRejectedRule[];
        // Client-side dedup: collapse records with same rule+wallet+date
        const seen = new Set<string>();
        const deduped = raw.filter(r => {
          const key = `${r.rule_id}::${r.matched_public_key}::${(r as any).rejected_at?.slice(0, 10) ?? ''}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setRules(deduped);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [mode, walletPublicKey]);

  useEffect(() => {
    load();
    if (pollIntervalMs > 0) {
      const id = setInterval(load, pollIntervalMs);
      return () => clearInterval(id);
    }
  }, [load, pollIntervalMs]);

  const handleReject = async (rule: GeoLinkPendingRule) => {
    const key = `${rule.rule_id}-${rule.matched_public_key}`;
    setActioning(key);
    try {
      await rejectPendingRule(rule.rule_id, rule.matched_public_key);
      await load();
    } catch (err: any) {
      setError(err.message || 'Reject failed');
    } finally {
      setActioning(null);
    }
  };

  const isPending = (r: AnyRule): r is GeoLinkPendingRule  => mode === 'pending';
  const isCompleted = (r: AnyRule): r is GeoLinkCompletedRule => mode === 'completed';

  const getTimestamp = (r: AnyRule): string => {
    if (isCompleted(r)) return (r as GeoLinkCompletedRule).completed_at;
    return (r as any).rejected_at || (r as any).received_at || '';
  };

  const getExpandKey = (r: AnyRule) =>
    `${(r as any).rule_id}-${(r as any).matched_public_key}-${getTimestamp(r)}`;

  const explorerBase = network === 'mainnet'
    ? 'https://stellar.expert/explorer/public/tx'
    : 'https://stellar.expert/explorer/testnet/tx';

  return (
    <>
    <div style={{ background: C.bg, minHeight: '100%', padding: 0, color: C.text, fontFamily: 'inherit' }}>
      <style>{`
        .glrp-card { transition: background 0.15s; }
        .glrp-card:hover { background: ${C.bgCardHover} !important; }
        .glrp-btn-reject { background: transparent; color: ${C.red}; border: 1px solid ${C.red}55; border-radius: 5px; padding: 4px 10px; font-size: 11px; font-weight: 600; cursor: pointer; transition: background 0.15s; }
        .glrp-btn-reject:hover:not(:disabled) { background: ${C.redDim}; }
        .glrp-btn-reject:disabled { opacity: 0.4; cursor: not-allowed; }
        .glrp-refresh-btn { background: transparent; border: 1px solid ${C.border}; color: ${C.goldDark}; border-radius: 6px; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; transition: background 0.15s; }
        .glrp-refresh-btn:hover:not(:disabled) { background: ${C.goldDim}; }
      `}</style>

      {/* ── Header ────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '14px 16px 10px', borderBottom: `1px solid ${C.borderFaint}`,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: C.text }}>
              {cfg.icon} {cfg.label} Rules
            </span>
            {rules.length > 0 && (
              <span style={{
                background: cfg.dim, color: cfg.color,
                border: `1px solid ${cfg.border}`,
                borderRadius: '10px', padding: '1px 7px',
                fontSize: '10px', fontWeight: 700,
              }}>
                {rules.length}
              </span>
            )}
          </div>
          <div style={{ fontSize: '11px', color: C.textFaint, marginTop: '2px' }}>
            {loading ? '⟳ Loading…' : `Fetched from GeoLink · ${new Date().toLocaleTimeString()}`}
          </div>
        </div>
        <button
          className="glrp-refresh-btn"
          onClick={load}
          disabled={loading}
          title="Refresh"
        >
          ↻
        </button>
      </div>

      {/* ── Error ─────────────────────────────────────────────────────── */}
      {error && (
        <div style={{
          margin: '10px 14px', padding: '9px 12px', borderRadius: '7px',
          background: C.redDim, border: `1px solid ${C.red}44`, color: C.red, fontSize: '12px',
        }}>
          ⚠ {error}
        </div>
      )}

      {/* ── Rule List ─────────────────────────────────────────────────── */}
      <div style={{ padding: '10px 14px' }}>
        {!loading && rules.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '32px 16px',
            color: C.textFaint, fontSize: '12px', lineHeight: '1.8',
          }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>{cfg.icon}</div>
            {cfg.emptyMsg}
          </div>
        )}

        {rules.map((rule, idx) => {
          const expandKey = getExpandKey(rule);
          const isExpanded = expandedId === expandKey;
          const r = rule as any;
          const ts = getTimestamp(rule);
          const actionKey = `${r.rule_id}-${r.matched_public_key}`;
          const isActioning = actioning === actionKey;

          return (
            <div
              key={idx}
              className="glrp-card"
              style={{
                background: C.bgCard,
                border: `1px solid ${isExpanded ? cfg.color + '66' : C.borderFaint}`,
                borderLeft: `3px solid ${cfg.color}`,
                borderRadius: '8px',
                marginBottom: '8px',
                overflow: 'hidden',
              }}
            >
              {/* Collapsed row */}
              <div
                style={{ padding: '10px 12px', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setExpandedId(isExpanded ? null : expandKey)}
              >
                {/* Row 1: status badge + rule name + chevron */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                  <span style={{
                    background: cfg.dim, color: cfg.color,
                    border: `1px solid ${cfg.border}`,
                    borderRadius: '5px', padding: '2px 7px',
                    fontSize: '10px', fontWeight: 700, flexShrink: 0,
                  }}>
                    {cfg.icon} {cfg.label}
                  </span>
                  <span style={{
                    fontSize: '12px', fontWeight: 700, color: '#000',
                    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {r.rule_name || 'Unknown rule'}
                  </span>
                  <span style={{ fontSize: '11px', color: C.textFaint }}>{isExpanded ? '▲' : '▼'}</span>
                </div>

                {/* Row 2: function + wallet + time */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                  <span style={{
                    background: C.goldDim, color: C.goldDark,
                    borderRadius: '4px', padding: '1px 7px', fontSize: '10px', fontWeight: 600,
                    fontFamily: 'monospace',
                  }}>
                    {r.function_name || '—'}
                  </span>
                  <span style={{ fontSize: '11px', color: C.textFaint, fontFamily: 'monospace' }}>
                    {r.matched_public_key
                      ? `${r.matched_public_key.slice(0, 6)}…${r.matched_public_key.slice(-4)}`
                      : '—'}
                  </span>
                  {isCompleted(rule) && (rule as GeoLinkCompletedRule).transaction_hash && (
                    <span style={{
                      background: C.greenDim, color: C.green,
                      borderRadius: '4px', padding: '1px 7px', fontSize: '10px', fontWeight: 600,
                    }}>
                      ✓ tx
                    </span>
                  )}
                  <span style={{ fontSize: '10px', color: C.textFaint, marginLeft: 'auto' }}>
                    {ts ? new Date(ts).toLocaleString() : '—'}
                  </span>
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div style={{
                  borderTop: `1px solid ${C.borderFaint}`,
                  background: '#fff',
                  padding: '12px',
                }}>
                  {/* View on Map button */}
                  {r.location && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setRuleDetailInfo({
                          ruleId:          r.rule_id,
                          ruleName:        r.rule_name,
                          contractAddress: r.contract_address,
                          functionName:    r.function_name,
                          network,
                          ruleLocation:    r.location,
                          matchedAt:       r.received_at || null,
                          status:          isCompleted(rule) ? 'completed' : mode === 'rejected' ? 'rejected' : 'pending',
                          completedAt:     isCompleted(rule) ? (rule as GeoLinkCompletedRule).completed_at : undefined,
                          transactionHash: isCompleted(rule) ? (rule as GeoLinkCompletedRule).transaction_hash : undefined,
                          rejectedAt:      !isCompleted(rule) ? r.rejected_at : undefined,
                          rejectedReason:  r.reason || undefined,
                          // Pass rule-type fields so the marker is correct before the full-rule fetch
                          ruleType:        r.rule_type   || null,
                          radiusMeters:    r.radius_meters != null ? Number(r.radius_meters) : null,
                          boundaryGeoJson: r.boundary_geojson || null,
                        });
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '5px',
                        marginBottom: '10px', padding: '6px 12px', borderRadius: '7px',
                        border: '1px solid #1a1f2e44', background: '#1a1f2e08',
                        color: '#1a1f2e', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      🗺 View Rule on Map
                    </button>
                  )}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: '6px 16px', marginBottom: '12px',
                  }}>
                    {([
                      ['Rule ID',        r.rule_id],
                      ['Function',       r.function_name],
                      r.amount ? ['Amount', `${(Number(r.amount) / 1e7).toFixed(7)} XLM`] : null,
                      ['Contract',       r.contract_address],
                      ['Matched Wallet', r.matched_public_key],
                      r.location ? ['Location', `${r.location.latitude?.toFixed(4)}, ${r.location.longitude?.toFixed(4)}`] : null,
                      isCompleted(rule) ? ['Completed at', new Date((rule as GeoLinkCompletedRule).completed_at).toLocaleString()] : null,
                      isCompleted(rule) && (rule as GeoLinkCompletedRule).transaction_hash
                        ? ['Tx Hash', (rule as GeoLinkCompletedRule).transaction_hash]
                        : null,
                      !isCompleted(rule) && r.rejected_at ? ['Rejected at', new Date(r.rejected_at).toLocaleString()] : null,
                      !isCompleted(rule) && r.reason ? ['Reason', r.reason] : null,
                      !isCompleted(rule) && r.received_at ? ['Received at', new Date(r.received_at).toLocaleString()] : null,
                    ] as any[]).filter(Boolean).map(([label, value]: [string, any]) => (
                      <div key={label}>
                        <div style={{ fontSize: '9px', color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>{label}</div>
                        <div style={{ fontSize: '11px', color: '#333', fontFamily: 'monospace', wordBreak: 'break-all' }}>{String(value ?? '—')}</div>
                      </div>
                    ))}
                  </div>

                  {/* Stellar Expert link for completed rules */}
                  {isCompleted(rule) && (rule as GeoLinkCompletedRule).transaction_hash && (
                    <div style={{ marginBottom: '10px' }}>
                      <a
                        href={`${explorerBase}/${(rule as GeoLinkCompletedRule).transaction_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '11px', color: C.goldDark, textDecoration: 'none', fontWeight: 600 }}
                      >
                        ↗ View on Stellar Expert
                      </a>
                    </div>
                  )}

                  {/* Parameters block */}
                  {r.parameters && Object.keys(r.parameters).length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '10px', color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '5px' }}>
                        Function Parameters
                      </div>
                      <pre style={{
                        background: '#f5f5f5', border: `1px solid ${C.borderFaint}`,
                        borderRadius: '6px', padding: '8px 10px',
                        fontSize: '10px', color: '#333', margin: 0,
                        overflow: 'auto', maxHeight: '140px',
                        fontFamily: 'monospace', lineHeight: '1.5',
                      }}>
                        {JSON.stringify(r.parameters, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Message (for pending rules) */}
                  {isPending(rule) && r.message && (
                    <div style={{
                      padding: '8px 10px', borderRadius: '6px',
                      background: C.blueDim, border: `1px solid ${C.blue}33`,
                      color: C.blue, fontSize: '11px', marginBottom: '10px',
                    }}>
                      ℹ {r.message}
                    </div>
                  )}

                  {/* Actions for pending rules */}
                  {isPending(rule) && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="glrp-btn-reject"
                        disabled={isActioning}
                        onClick={() => handleReject(rule as GeoLinkPendingRule)}
                      >
                        {isActioning ? '…' : '✕ Reject'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>

    {ruleDetailInfo && (
      <RuleMatchDetailOverlay
        isOpen={!!ruleDetailInfo}
        onClose={() => setRuleDetailInfo(null)}
        info={ruleDetailInfo}
        network={network}
        allowedCountries={allowedCountries}
      />
    )}
    </>
  );
};
